import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  {
    variants: {
      variant: {
        // Ação primária do DS = --mp-accent (#6a55d7), hover --mp-accent-hover (#5742c4).
        // NÃO usar `bg-primary`: --primary é hsl(258 51% 20%) = #29194d, um navy que o
        // /design-system não autoriza para botão (DSFoundationsColors: o grupo Brand tem
        // apenas --mp-accent). Travado em src/lib/__tests__/design-system.test.ts.
        default: "bg-accent text-accent-foreground shadow-sm hover:bg-[var(--mp-accent-hover)]",
        /** @deprecated Alias de `default` — era o contorno de quando `default` saía navy. Use `default`. */
        accent: "bg-accent text-accent-foreground shadow-sm hover:bg-[var(--mp-accent-hover)]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-[#b91c1c]",
        // DS `secondary`: fundo inalterado no hover; só borda e texto viram accent.
        // `hover:bg-accent` era herança do shadcn, onde --accent é superfície de hover
        // suave — aqui --accent é o roxo da marca, então pintava o botão de roxo sólido.
        outline:
          "border border-input bg-background shadow-sm hover:border-accent hover:text-accent",
        /** @deprecated Não existe no /design-system (é um terceiro tom de preenchimento). Use `outline`. */
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        // DS `ghost`: hover é --mp-asoft (accent a 8%) + texto accent, nunca roxo sólido.
        ghost:
          "hover:bg-[var(--mp-asoft)] hover:text-accent",
        success:
          "bg-[var(--mp-pos)] text-white shadow-sm hover:bg-[#15803d]",
        link: "text-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
